// «Convertir a cliente» con proceso en vivo: la pantalla de una alta y el enlace a ella desde las fichas.
// La alta la ejecuta el worker del VPS (scripts/altas/reclutify-altas.js) paso por paso; aquí solo se LEE
// (RLS: solo internos) y se aprietan los botones de las personas (reintentar, mandar invitación, bot listo),
// que son funciones SQL. Se actualiza sola: Supabase Realtime + sondeo cada 2.5 s por si Realtime no llega.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Circle, ExternalLink, Hand, Loader2, RotateCcw, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Link, useParams } from "react-router";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Button } from "@/components/ui/button";
import { MODO } from "@/reclutamiento/modo";
import { cn } from "@/lib/utils";
import { Encabezado, Pildora } from "../comun";

export type EstadoPaso = "pendiente" | "corriendo" | "hecho" | "falla" | "espera_persona";
export type EstadoAlta = "pendiente" | "corriendo" | "espera_persona" | "falla" | "lista";
export type Alta = {
  id: string;
  deal_id: number | null;
  company_id: number | null;
  cliente_id: string | null;
  slug: string;
  correo: string;
  puesto: string;
  estado: EstadoAlta;
  creado_en: string;
  actualizado_en: string;
  terminado_en: string | null;
};
export type PasoAlta = {
  alta_id: string;
  orden: number;
  clave: string;
  titulo: string;
  estado: EstadoPaso;
  detalle: string;
  intentos: number;
  empezo_en: string | null;
  termino_en: string | null;
  actualizado_en: string;
};

export const urlPortal = (slug: string) => `https://rh.${slug}.auteam.io/`;

const ESTADO_ALTA: Record<EstadoAlta, [string, "ok" | "revisar" | "bad" | "neutro" | "acento"]> = {
  pendiente: ["En cola", "neutro"],
  corriendo: ["Trabajando", "acento"],
  espera_persona: ["Te toca", "revisar"],
  falla: ["Se atoró", "bad"],
  lista: ["Lista", "ok"],
};

const horaDe = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString("es-MX", { timeZone: "America/Monterrey", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "";

/** La alta de un prospecto o de un cliente (null si no tiene). Solo en la instalación con Supabase. */
export function useAltaDe(campo: "deal_id" | "cliente_id", valor: string | number | null | undefined) {
  return useQuery({
    queryKey: ["alta-de", campo, valor],
    enabled: MODO === "supabase" && valor != null && valor !== "",
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("altas_cliente")
        .select("id, estado, slug")
        .eq(campo, valor as string)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as Pick<Alta, "id" | "estado" | "slug"> | null;
    },
  });
}

/** Tarjeta chica con el enlace a la alta en vivo (fichas del prospecto y del cliente). */
export const EnlaceAlta = ({ alta }: { alta: Pick<Alta, "id" | "estado" | "slug"> }) => {
  const [t, tono] = ESTADO_ALTA[alta.estado];
  return (
    <div className="rounded-xl border p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Alta del cliente</h3>
        <Pildora tono={tono}>{t}</Pildora>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">Portal: {urlPortal(alta.slug)}</p>
      <Button asChild size="sm" variant="outline">
        <Link to={`/altas/${alta.id}`}>Ver el proceso en vivo</Link>
      </Button>
    </div>
  );
};

function useAltaEnVivo(id: string) {
  const qc = useQueryClient();
  const [enVivo, setEnVivo] = useState(false);
  const q = useQuery({
    queryKey: ["alta", id],
    queryFn: async () => {
      const sb = getSupabaseClient();
      const [a, p] = await Promise.all([
        sb.from("altas_cliente").select("*").eq("id", id).maybeSingle(),
        sb.from("altas_pasos").select("*").eq("alta_id", id).order("orden"),
      ]);
      if (a.error) throw new Error(a.error.message);
      if (p.error) throw new Error(p.error.message);
      return { alta: a.data as Alta | null, pasos: (p.data ?? []) as PasoAlta[], leido: new Date().toISOString() };
    },
    // sondeo: siempre que no esté lista (si Realtime llega, solo adelanta la lectura)
    refetchInterval: (query) => (query.state.data?.alta?.estado === "lista" ? false : 2500),
    refetchIntervalInBackground: false,
  });
  useEffect(() => {
    const sb = getSupabaseClient();
    const recargar = () => qc.invalidateQueries({ queryKey: ["alta", id] });
    const canal = sb
      .channel(`alta-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "altas_pasos", filter: `alta_id=eq.${id}` }, recargar)
      .on("postgres_changes", { event: "*", schema: "public", table: "altas_cliente", filter: `id=eq.${id}` }, recargar)
      .subscribe((estado) => setEnVivo(estado === "SUBSCRIBED"));
    return () => {
      sb.removeChannel(canal);
    };
  }, [id, qc]);
  return { ...q, enVivo };
}

const IconoPaso = ({ estado }: { estado: EstadoPaso }) => {
  const base = "flex size-7 shrink-0 items-center justify-center rounded-full";
  if (estado === "hecho")
    return (
      <span className={cn(base, "bg-[var(--ok)] text-white")} aria-label="Hecho">
        <Check className="size-4" strokeWidth={3} />
      </span>
    );
  if (estado === "corriendo")
    return (
      <span className={cn(base, "bg-[var(--acento-soft)] text-[var(--acento)]")} aria-label="Trabajando">
        <Loader2 className="size-4 animate-spin" strokeWidth={3} />
      </span>
    );
  if (estado === "falla")
    return (
      <span className={cn(base, "bg-[var(--bad)] text-white")} aria-label="Falló">
        <X className="size-4" strokeWidth={3} />
      </span>
    );
  if (estado === "espera_persona")
    return (
      <span className={cn(base, "bg-[var(--warn)] text-[var(--warn-glyph)]")} aria-label="Te toca">
        <Hand className="size-4" strokeWidth={2.5} />
      </span>
    );
  return (
    <span className={cn(base, "border text-muted-foreground")} aria-label="Pendiente">
      <Circle className="size-3" />
    </span>
  );
};

const Paso = ({ paso, alta, ultimo }: { paso: PasoAlta; alta: Alta; ultimo: boolean }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const accion = useMutation({
    mutationFn: async (fn: "alta_reintentar" | "alta_pedir_invitacion" | "alta_bot_listo") => {
      const args = fn === "alta_reintentar" ? { p_alta_id: alta.id, p_clave: paso.clave } : { p_alta_id: alta.id };
      const { error } = await getSupabaseClient().rpc(fn, args);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      setConfirmar(false);
      qc.invalidateQueries({ queryKey: ["alta", alta.id] });
    },
    onError: (e: Error) => notify(e.message, { type: "error" }),
  });
  const hora = paso.estado === "hecho" || paso.estado === "falla" ? paso.termino_en : paso.empezo_en;
  return (
    <li className="relative flex gap-3 pb-5" data-paso={paso.clave} data-estado={paso.estado}>
      {!ultimo ? <span className="absolute top-8 bottom-0 left-[13px] w-px bg-border" aria-hidden /> : null}
      <IconoPaso estado={paso.estado} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className={cn("font-medium", paso.estado === "pendiente" && "text-muted-foreground")}>
            {paso.orden}. {paso.titulo}
          </p>
          {paso.estado === "espera_persona" ? <Pildora tono="revisar">Te toca</Pildora> : null}
          {paso.estado === "falla" ? <Pildora tono="bad">Falló</Pildora> : null}
          {hora ? <span className="num text-xs text-muted-foreground">{horaDe(hora)}</span> : null}
          {paso.intentos > 1 ? <span className="text-xs text-muted-foreground">· intento {paso.intentos}</span> : null}
        </div>
        {paso.detalle ? (
          <p
            className={cn(
              "mt-1 text-sm break-words whitespace-pre-wrap",
              paso.estado === "falla" ? "text-[var(--bad-ink)]" : "text-muted-foreground",
            )}
          >
            {paso.detalle}
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-2">
          {paso.estado === "falla" ? (
            <Button size="sm" variant="outline" disabled={accion.isPending} onClick={() => accion.mutate("alta_reintentar")}>
              <RotateCcw className="size-4" /> Reintentar
            </Button>
          ) : null}
          {paso.clave === "invitacion" && paso.estado === "espera_persona" ? (
            confirmar ? (
              <>
                <Button size="sm" disabled={accion.isPending} onClick={() => accion.mutate("alta_pedir_invitacion")}>
                  <Send className="size-4" /> Sí, mandar el correo a {alta.correo}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmar(false)}>
                  Todavía no
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => setConfirmar(true)}>
                <Send className="size-4" /> Enviar invitación
              </Button>
            )
          ) : null}
          {paso.clave === "bot" && paso.estado === "espera_persona" ? (
            <Button size="sm" variant="outline" disabled={accion.isPending} onClick={() => accion.mutate("alta_bot_listo")}>
              <Check className="size-4" /> Ya quedó el bot
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
};

/** Pantalla /altas/:id */
export const AltaEnVivo = () => {
  const { id } = useParams();
  if (MODO !== "supabase")
    return (
      <p className="text-sm text-muted-foreground">
        El alta automática solo corre en la instalación con Supabase (en la demo no hay servidor que cree DNS ni portal).
      </p>
    );
  return <AltaEnVivoSupabase id={id!} />;
};

const AltaEnVivoSupabase = ({ id }: { id: string }) => {
  const { data, isPending, error, refetch, enVivo } = useAltaEnVivo(id);
  if (error)
    return (
      <p className="text-sm text-muted-foreground">
        No se pudo leer la alta ({(error as Error).message}).{" "}
        <button type="button" className="underline" onClick={() => refetch()}>
          Reintentar
        </button>
      </p>
    );
  if (isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  const { alta, pasos, leido } = data;
  if (!alta) return <p>No existe esa alta.</p>;
  const hechos = pasos.filter((p) => p.estado === "hecho").length;
  const [t, tono] = ESTADO_ALTA[alta.estado];
  const portal = urlPortal(alta.slug);
  const portalListo = pasos.some((p) => p.clave === "probar" && p.estado === "hecho");
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-3 flex items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={alta.deal_id ? `/deals/${alta.deal_id}/show` : "/clientes"}>
            <ArrowLeft className="size-4" /> {alta.deal_id ? "Prospecto" : "Clientes"}
          </Link>
        </Button>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Se actualiza sola">
          <span className={cn("size-2 rounded-full", enVivo ? "bg-[var(--ok)]" : "bg-[var(--warn)]")} />
          {enVivo ? "En vivo" : "Actualizando cada 2 s"} · {horaDe(leido)}
        </span>
      </div>
      <Encabezado
        ceja="Convertir a cliente"
        titulo={`Alta de «${alta.slug}»`}
        sub={
          <>
            Primera vacante: {alta.puesto} · usuario del portal: {alta.correo}
          </>
        }
        acciones={<Pildora tono={tono}>{t}</Pildora>}
      />
      <div className="mb-4 rounded-xl border bg-card p-4">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span>
            {hechos} de {pasos.length} pasos
          </span>
          {portalListo ? (
            <a className="flex items-center gap-1 underline" href={portal} target="_blank" rel="noopener noreferrer">
              {portal} <ExternalLink className="size-3.5" />
            </a>
          ) : (
            <span className="text-muted-foreground">{portal}</span>
          )}
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={hechos} aria-valuemax={pasos.length}>
          <div
            className={cn("h-full transition-all", alta.estado === "falla" ? "bg-[var(--bad)]" : "bg-[var(--ok)]")}
            style={{ width: `${pasos.length ? (100 * hechos) / pasos.length : 0}%` }}
          />
        </div>
        {alta.estado === "pendiente" && hechos === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">El servidor la toma en unos segundos.</p>
        ) : null}
        {alta.cliente_id ? (
          <p className="mt-2 text-sm">
            <Link className="underline" to={`/clientes/${alta.cliente_id}/show`}>
              Abrir la ficha del cliente
            </Link>
          </p>
        ) : null}
      </div>
      <ol className="rounded-xl border bg-card p-4">
        {pasos.map((p, i) => (
          <Paso key={p.clave} paso={p} alta={alta} ultimo={i === pasos.length - 1} />
        ))}
      </ol>
    </div>
  );
};
